import { useState, useEffect } from "react";
import {
    CheckCircleIcon,
    Cog6ToothIcon,
    ExclamationCircleIcon,
    ArrowDownIcon,
    PencilSquareIcon,
    TrashIcon,
    XCircleIcon,
} from "@heroicons/react/24/solid";

export default function SubstepViewer({ taskData }) {
    const [substeps, setSubsteps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editingSubstep, setEditingSubstep] = useState(null);
    const [form, setForm] = useState({
        name: "",
        step: "",
        description: "",
        type: "Action",
    });

        const fetchSubsteps = async () => {
                        try {
                const response = await fetch(`https://api.insightos.co.uk/api/getsubsteps/${taskData.id}`, {
                    method: "GET",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    credentials: 'include',

                });

                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
                const substeps = await response.json();

                console.log("substeps:", substeps);

                substeps.data.sort((a, b) => a.Step - b.Step);
                setSubsteps(substeps.data);
            } catch (error) {
                console.error("Failed to fetch substeps:", error);
                setError(error);
            } finally {
                setLoading(false);
            }
        };

        useEffect(() => {

        fetchSubsteps();
    }, [taskData.id]);


    if (loading) return <p>Loading...</p>;
    if (error) return <p>Error: {error.message}</p>;

    const typeIcons = {
        Action: <Cog6ToothIcon className="h-8 w-8 text-blue-500" />,
        Bridge: <ExclamationCircleIcon className="h-8 w-8 text-yellow-500" />,
        Expectation: <CheckCircleIcon className="h-8 w-8 text-purple-500" />,
    };

    const handleEdit = (substep) => {
        setEditingSubstep(substep.id);
        setForm({
            name: substep.Name,
            step: substep.Step,
            description: substep.Description,
            type: substep.Type,
        });
    };

    const handleCloseEdit = () => {
        setEditingSubstep(null);
    };

    const handleDelete = async (id) => {
        
        try {
            const response = await fetch(`https://api.insightos.co.uk/api/deletesubsteps/${id}`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: 'include',

            });

            if (!response.ok) throw new Error("Failed to delete substep");

            fetchSubsteps();

        } catch (error) {
            console.error("Error deleting substep:", error);
        }
    };

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSave = async (e) => {
        e.preventDefault();
        
        try {
            const response = await fetch(`https://api.insightos.co.uk/api/updatesubstep/${editingSubstep}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: 'include',
                body: JSON.stringify({
                    Name: form.name,
                    Step: form.step,
                    Description: form.description,
                    Type: form.type,
                }),
            });

            if (!response.ok) throw new Error("Failed to update substep");

            const updatedSubstep = await response.json();

            fetchSubsteps();



            setEditingSubstep(null);
        } catch (error) {
            console.error("Error updating substep:", error);
        }
    };

    return (
        <div className="flex flex-col items-center space-y-8">
            <h3 className="text-xl font-semibold text-gray-900">Substeps</h3>
            <ul className="flex flex-col items-center space-y-8">
                {substeps.map((substep, index) => (
                    <div key={substep.id} className="flex flex-col items-center">
                        <div className="flex items-center justify-center w-12 h-12 rounded-full bg-emerald-500 text-gray-900 text-xl font-bold">
                            {substep.Step}
                        </div>

                        <div className="flex flex-col items-center text-center mt-2">
                            {typeIcons[substep.Type]}
                            <p className="text-lg font-medium text-gray-900 mt-2">{substep.Name}</p>
                            <p className="text-sm text-gray-500">{substep.Description}</p>
                        </div>

                        <div className="flex space-x-4 mt-2">
                            <button onClick={() => handleEdit(substep)} className="text-blue-600 hover:text-blue-800 flex items-center">
                                <PencilSquareIcon className="h-5 w-5" /> <span className="ml-1">Edit</span>
                            </button>
                            <button onClick={() => handleDelete(substep.id)} className="text-red-600 hover:text-red-800 flex items-center">
                                <TrashIcon className="h-5 w-5" /> <span className="ml-1">Delete</span>
                            </button>
                        </div>

                        {editingSubstep === substep.id && (
                            <form onSubmit={handleSave} className="space-y-4 mt-4 w-80">
                                <div>
                                    <label className="block text-sm font-medium text-gray-900">Name</label>
                                    <input
                                        name="name"
                                        value={form.name}
                                        onChange={handleChange}
                                        type="text"
                                        required
                                        className="block w-full px-3 py-2 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-900">Step</label>
                                    <input
                                        name="step"
                                        value={form.step}
                                        onChange={handleChange}
                                        type="number"
                                        required
                                        className="block w-full px-3 py-2 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-900">Description</label>
                                    <textarea
                                        name="description"
                                        value={form.description}
                                        onChange={handleChange}
                                        rows="4"
                                        required
                                        className="block w-full px-3 py-2 border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                    />
                                </div>
                                <div className="flex justify-between">
                                    <button
                                        type="submit"
                                        className="rounded-md bg-indigo-600 px-3 py-2 text-white shadow-sm hover:bg-indigo-500"
                                        data-component="substeps-editor"
                                        data-time-saved="5"
                                        data-feature="save_substep_put"
                                    >
                                        Save
                                    </button>
                                    <button onClick={handleCloseEdit} type="button" className="text-gray-600 hover:text-gray-800 flex items-center">
                                        <XCircleIcon className="h-5 w-5 mr-1" /> Cancel Edit
                                    </button>
                                </div>
                            </form>
                        )}

                        {index < substeps.length - 1 && <ArrowDownIcon className="h-8 w-8 text-indigo-600 animate-bounce mt-4" />}
                    </div>
                ))}
            </ul>
        </div>
    );
}
